<template>
	<div class="w-100">
		<v-table class="comparison-table">
			<!-- HEADER -->
			<thead>
				<tr class="table-header-row">
					<!-- Empty top-left cell (aligns with feature column) -->
					<th class="table-header-cell feature-header"></th>

					<!-- ChatGPT Column Header -->
					<th class="table-header-cell vendor-header">
						<div class="vendor-pill">
							<img src="../../assets/chatgpt.png" alt="ChatGPT" class="vendor-logo" />
						</div>
					</th>

					<!-- Agency Column Header -->
					<th class="table-header-cell vendor-header">
						<div class="vendor-pill">
							<v-icon size="28">mdi-office-building</v-icon>
						</div>
					</th>

					<!-- aigenerator Column Header -->
					<th class="table-header-cell vendor-header">
						<div class="vendor-pill">
							<v-icon size="28">mdi-robot</v-icon>
						</div>
					</th>
				</tr>
			</thead>

			<!-- BODY -->
			<tbody>
				<tr v-for="row in rows" :key="row.feature" class="table-body-row">
					<!-- FEATURE COLUMN (icon + text) -->
					<td class="table-body-cell feature-cell">
						<v-icon size="20" class="feature-icon">{{ row.icon }}</v-icon>
						<span class="feature-text">{{ row.feature }}</span>
					</td>

					<!-- CHATGPT VALUE -->
					<td class="table-body-cell value-cell text-center">
						<v-icon :color="row.chatgpt ? 'success' : 'error'">
							{{ row.chatgpt ? "mdi-check" : "mdi-close" }}
						</v-icon>
					</td>

					<!-- AGENCY VALUE -->
					<td class="table-body-cell value-cell text-center">
						<v-icon :color="row.agency ? 'success' : 'error'">
							{{ row.agency ? "mdi-check" : "mdi-close" }}
						</v-icon>
					</td>

					<!-- aigenerator VALUE -->
					<td class="table-body-cell value-cell text-center">
						<v-icon :color="row.aigenerator ? 'success' : 'error'">
							{{ row.aigenerator ? "mdi-check" : "mdi-close" }}
						</v-icon>
					</td>
				</tr>
			</tbody>

			<!-- FOOTER (Vendor Names) -->
			<tfoot>
				<tr class="table-footer-row">
					<td class="table-footer-cell feature-footer"></td>
					<td class="table-footer-cell text-center">ChatGPT</td>
					<td class="table-footer-cell text-center">Agency</td>
					<td class="table-footer-cell text-center">AIGenerator.com</td>
				</tr>
			</tfoot>
		</v-table>
	</div>
</template>

<script setup>
	import { ref } from "vue"

	/**
	 * Each object in rows represents one row of the comparison table.
	 * - `icon`: an MDI icon key for that feature
	 * - `feature`: the label text on the left
	 * - Booleans (chatgpt, agency, aigenerator) decide whether to show mdi-check (green) or mdi-close (red)
	 */
	const rows = ref([
		{
			icon: "mdi-briefcase-outline",
			feature: "Knows your business",
			chatgpt: false,
			agency: true,
			aigenerator: true,
		},
		{
			icon: "mdi-timer-outline",
			feature: "Instant turnaround",
			chatgpt: true,
			agency: false,
			aigenerator: true,
		},
		{
			icon: "mdi-currency-usd",
			feature: "Affordable",
			chatgpt: true,
			agency: false,
			aigenerator: true,
		},
		{
			icon: "mdi-lightbulb-on-outline",
			feature: "Built-in strategy",
			chatgpt: false,
			agency: true,
			aigenerator: true,
		},
	])
</script>

<style scoped>
	/* 1. Horizontal scrolling wrapper on small screens */
	.overflow-x-auto {
		overflow-x: auto;
	}

	/* 2. Make the Vuetify v-table fill its container */
	.comparison-table {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0;
		max-width: 1000px;
		margin: 0 auto;
		border-radius: 8px;
	}

	.table-header-row {
		background-color: rgba(224, 219, 246, 0.88);
		border-top-left-radius: 8px;
		border-top-right-radius: 8px;
	}

	.table-header-cell {
		padding: 12px 8px; /* uniform padding for perfect alignment */
		border: none;
		vertical-align: middle;
	}

	.feature-header {
		width: 40%;
	}

	.vendor-header {
		width: 20%;
	}

	/* Round only the top-left and top-right corners */
	.table-header-row th:first-of-type {
		border-top-left-radius: 8px;
	}

	.table-header-row th:last-of-type {
		border-top-right-radius: 8px;
	}

	/* 4. “Pill” behind each vendor logo/icon */
	.vendor-pill {
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: #fefefe;
		border-radius: 6px;
		padding: 6px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
	}

	.vendor-logo {
		width: 25px;
		height: auto;
	}

	/* 5. BODY ROWS: single border on top of each row (except first) */
	.table-body-row {
		border-top: 1px solid #ddd; /* light gray divider */
	}

	.table-body-row:first-of-type {
		border-top: none;
	}

	.table-body-cell {
		padding: 12px 8px;
		vertical-align: middle;
	}

	.feature-cell {
		display: flex;
		align-items: center;
	}

	.feature-icon {
		margin-right: 20px;
		margin-left: 5px;
		color: #6b46c1;
	}

	.feature-text {
		color: #1f1f1f;
		white-space: nowrap;
	}

	.table-footer-row {
		background-color: white;
		border-top: 2px solid #e0dbf6;
	}

	.table-footer-cell {
		padding: 10px 8px;
	}

	.comparison-table th:first-of-type,
	.comparison-table th:last-of-type {
		position: sticky;
		top: 0;
		z-index: 2;
	}
</style>
